import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sets or removes a query param on a URLSearchParams in place.
 *
 * @example
 * writeParam(params, 'merchant', 'Billa') // sets merchant=Billa
 * writeParam(params, 'merchant', undefined) // removes merchant
 */
export function writeParam(
  params: URLSearchParams,
  key: string,
  value: string | undefined
): void {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

type Mutation = (params: URLSearchParams) => void;

// Writes queued in the same tick are applied in one history replace. React
// Router's functional setter reads the params of the last render, so two
// separate calls in one tick would each start from the same stale params and
// the last one would win. Module level so writes from different hook instances
// (a filter hook and a pagination hook on the same page) batch together too.
let pendingMutations: Mutation[] = [];

/**
 * Read and batched write access to the URL query, shared by the table state
 * hooks. Every write replaces the current history entry, so the table page
 * stays one entry showing its latest filter, sort and page state, and Back
 * and Forward only move between pages. Writes queued in the same tick become
 * one replace.
 *
 * @example
 * const { searchParams, update } = useUrlParams();
 * update((params) => writeParam(params, 'merchant', 'Billa'));
 */
export function useUrlParams(): {
  searchParams: URLSearchParams;
  update: (mutate: Mutation) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const update = useCallback(
    (mutate: Mutation) => {
      pendingMutations.push(mutate);
      if (pendingMutations.length > 1) {
        return;
      }
      queueMicrotask(() => {
        const mutations = pendingMutations;
        pendingMutations = [];
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            mutations.forEach((mutation) => mutation(next));
            return next;
          },
          { replace: true }
        );
      });
    },
    [setSearchParams]
  );

  return useMemo(() => ({ searchParams, update }), [searchParams, update]);
}

const PAGE_KEY = 'page';
const PAGE_SIZE_KEY = 'pageSize';
const SORT_KEY = 'sort';
const ORDER_KEY = 'order';

export type UrlSortDirection = 'asc' | 'desc';
export interface UrlSort<Column extends string> {
  column: Column;
  direction: UrlSortDirection;
}

/**
 * Drops the page param so the next read is page 0. Call it in the same
 * `update` as a filter or sort write so both land in one history entry.
 *
 * @example
 * update((params) => { writeParam(params, 'merchant', value); resetPage(params); })
 */
export function resetPage(params: URLSearchParams): void {
  params.delete(PAGE_KEY);
}

const readInt = (
  value: string | null,
  fallback: number,
  min: number
): number => {
  const parsed = Number(value);
  return value !== null && Number.isInteger(parsed) && parsed >= min
    ? parsed
    : fallback;
};

interface UrlTableStateOptions<Column extends string> {
  defaultPageSize: number;
  /** Allowed sort columns. A URL value outside this list falls back to the default sort. */
  sortColumns?: readonly Column[];
  defaultSort?: UrlSort<Column>;
}

export interface UrlTableState<Column extends string> {
  page: number;
  pageSize: number;
  sort: UrlSort<Column> | undefined;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSort: (sort: UrlSort<Column>) => void;
}

/**
 * Page, page size and sort of a table kept in the URL query, next to the
 * table's filters. Changing the page size or sort goes back to page 0.
 * Defaults are left out of the URL so the plain route stays clean.
 *
 * @example
 * const table = useUrlTableState({
 *   defaultPageSize: 25,
 *   sortColumns: ['receipt_date', 'amount'],
 *   defaultSort: { column: 'receipt_date', direction: 'desc' },
 * });
 * pagination: { page: table.page, onPageChange: table.setPage, ... }
 */
export function useUrlTableState<Column extends string = never>({
  defaultPageSize,
  sortColumns,
  defaultSort,
}: UrlTableStateOptions<Column>): UrlTableState<Column> {
  const { searchParams, update } = useUrlParams();

  const page = readInt(searchParams.get(PAGE_KEY), 0, 0);
  // Flop rejects a page size of 0 with a 400.
  const pageSize = readInt(searchParams.get(PAGE_SIZE_KEY), defaultPageSize, 1);

  const sortParam = searchParams.get(SORT_KEY);
  const orderParam = searchParams.get(ORDER_KEY);
  const sort = useMemo<UrlSort<Column> | undefined>(() => {
    const column = sortColumns?.find((candidate) => candidate === sortParam);
    if (!column) {
      return defaultSort;
    }
    return {
      column,
      direction: orderParam === 'asc' ? 'asc' : 'desc',
    };
  }, [sortColumns, sortParam, orderParam, defaultSort]);

  const setPage = useCallback(
    (value: number) =>
      update((params) =>
        writeParam(params, PAGE_KEY, value > 0 ? String(value) : undefined)
      ),
    [update]
  );

  const setPageSize = useCallback(
    (value: number) =>
      update((params) => {
        writeParam(
          params,
          PAGE_SIZE_KEY,
          value === defaultPageSize ? undefined : String(value)
        );
        resetPage(params);
      }),
    [update, defaultPageSize]
  );

  const setSort = useCallback(
    (value: UrlSort<Column>) =>
      update((params) => {
        const isDefault =
          value.column === defaultSort?.column &&
          value.direction === defaultSort.direction;
        writeParam(params, SORT_KEY, isDefault ? undefined : value.column);
        writeParam(params, ORDER_KEY, isDefault ? undefined : value.direction);
        resetPage(params);
      }),
    [update, defaultSort]
  );

  return useMemo(
    () => ({ page, pageSize, sort, setPage, setPageSize, setSort }),
    [page, pageSize, sort, setPage, setPageSize, setSort]
  );
}
